import { useState, useCallback } from 'react'
import { ModalHeader, ModalBody, Button } from 'reactstrap'
import { useSelector, useDispatch } from 'react-redux'
import SubHeading from 'e-submission/components/Heading/SubHeading'
import ExpandableDuplicateField from './ExpandableDuplicateField'
import ResendLinkSection from './ResendLinkSection'
import { InfoTooltip } from './InfoTooltip'
import { SpanTextSize } from 'common-components/text'

import { getCurrentApp } from '../../../apps/selectors'
import { getCitizenId, getPassport, isPayerNeeded } from 'e-submission/domain/data-model/form/selectors'
import {
  createCaseEkyc,
  removeError,
  reuseCaseEkyc,
  setReuseEkycSelected,
} from '../../../apps/actions'
import { getIcon } from 'e-submission/components/Icon'
import { Modal } from 'common-components/index'
import styled from 'styled-components'

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const Footer = styled.div`
  display: flex;
  padding-top: 1rem;
  justify-content: center;
`

const MarginRigthBtn = styled(Button)`
  margin-right: 1rem;
`
const CenteredModalHeader = styled(ModalHeader)`
  .modal-title {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`

const SubmitButton = (props) => {
  const { onExit, handlerConfirm } = props

  return (
    <Footer>
      <MarginRigthBtn outline color="primary" onClick={() => onExit()}>
        ยกเลิก
      </MarginRigthBtn>
      <Button color="primary" onClick={() => handlerConfirm()}>
        ยืนยัน
      </Button>
    </Footer>
  )
}

export const DuplicateRMSCaseModal = ({
  isOpen,
  duplicateCase,
  seletedOption,
  setOption,
  handleOnExistDuplicateModal,
  handlerConfirm,
  hasPayer,
  insuredOrPayer,
  isNeedConfirmationLetter,
}) => {
  return (
    <Modal
      className={'duplicate-ekyc-modal'}
      isOpen={isOpen}
      size="md"
      modalClassName="default-bootstrap remote-selling-modal"
      toggle={() => {
        handleOnExistDuplicateModal()
      }}
    >
      <CenteredModalHeader>แจ้งเตือนเคส eKYC</CenteredModalHeader>
      <ModalBody className="form-group">
        <TitleSection>
          {getIcon('rmsDuplicate')}
          <SubHeading style={{ color: '#2425aa' }}>ตรวจพบมีข้อมูลการทำ eKYC ต้องการใช้ข้อมูลเดิมหรือไม่</SubHeading>
        </TitleSection>
        <ExpandableDuplicateField cases={duplicateCase} seletedOption={seletedOption} setOption={setOption} />
        <InfoTooltip>
          <SpanTextSize>โปรดตรวจสอบวันหมดอายุของเอกสารทุกครั้ง</SpanTextSize>
        </InfoTooltip>
        {seletedOption === 'new-case' && <ResendLinkSection />}
        {/* new resend link to confiamation letter */}
        {seletedOption === 'reuse' && isNeedConfirmationLetter && <ResendLinkSection />}
        <SubmitButton onExit={handleOnExistDuplicateModal} handlerConfirm={handlerConfirm} />
      </ModalBody>
    </Modal>
  )
}

const mapStateToProps = (state) => {
  const app = getCurrentApp(state)

  const insuredId = getCitizenId('insured')(app)
  const payerId = getCitizenId('payer')(app)
  const insuredPassport = getPassport('insured')(app)
  const payerPassport = getPassport('payer')(app)
  const hasPayer = isPayerNeeded(app)

  return {
    citizenId: { insured: insuredId, payer: payerId },
    passportNumber: { insured: insuredPassport, payer: payerPassport },
    hasPayer,
  }
}

const DuplicateRMSCaseContainer = ({
  isOpen,
  duplicateCase,
  insuredOrPayer,
  onExit,
  openIdentifyRespondingModal,
  closeIdentifyRespondingModal,
  ...props
}) => {
  const dispatch = useDispatch()
  const { citizenId, passportNumber, hasPayer } = useSelector(mapStateToProps)

  const [seletedOption, setOption] = useState('new-case')

  const createCase = useCallback(
    (id, passport) => dispatch(createCaseEkyc(insuredOrPayer, id, passport, true)),
    [dispatch, insuredOrPayer]
  )

  const reuseCaseEkycHandler = useCallback((proprietorId) => dispatch(reuseCaseEkyc(insuredOrPayer, proprietorId)), [
    dispatch,
    insuredOrPayer,
  ])

  const handleOnExistDuplicateModal = useCallback(() => {
    closeIdentifyRespondingModal()
    onExit()
    dispatch(removeError('EKYC_DUPLICATED_CITIZENID'))
  }, [closeIdentifyRespondingModal, dispatch, onExit])

  const setReuseEkycSelectedHandler = useCallback((value) => dispatch(setReuseEkycSelected(value)), [dispatch])

  const handlerConfirm = useCallback(() => {
    handleOnExistDuplicateModal()

    if (seletedOption === 'new-case') {
      createCase(citizenId[insuredOrPayer], passportNumber[insuredOrPayer])
    }

    if (seletedOption === 'reuse') {
      const reuseProprietorId = duplicateCase[0].id
      reuseCaseEkycHandler(reuseProprietorId)
      // TODO: handle submit link to get confirmation letter
      setReuseEkycSelectedHandler(true)
    } else {
      setReuseEkycSelectedHandler(false)
    }

    openIdentifyRespondingModal()
  }, [
    handleOnExistDuplicateModal,
    seletedOption,
    createCase,
    citizenId,
    insuredOrPayer,
    passportNumber,
    duplicateCase,
    reuseCaseEkycHandler,
    setReuseEkycSelectedHandler,
    openIdentifyRespondingModal,
  ])

  return (
    <DuplicateRMSCaseModal
      {...props}
      isOpen={isOpen}
      duplicateCase={duplicateCase}
      seletedOption={seletedOption}
      setOption={setOption}
      handleOnExistDuplicateModal={handleOnExistDuplicateModal}
      handlerConfirm={handlerConfirm}
      hasPayer={hasPayer}
      insuredOrPayer={insuredOrPayer}
    />
  )
}

export default DuplicateRMSCaseContainer
